import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../components/layout/Navbar";
import Footer from "../../components/layout/Footer";
import useHls from "../../utils/useHls";
import {
  fetchMovieDetail,
  fetchSimilarContent,
  fetchGenres,
  fetchAgeRatings,
  fetchTrailer,
  getImageUrl,
  addToWatchlist,
  removeFromWatchlist,
} from "../../redux/content";
import { useSelector } from "react-redux";
import { fetchPurchaseHistory } from "../../redux/subscription";
import { getClientApiUrl } from "../../config/apiConfig";
import "../../components/styles/movieDetailsPage.scss";

/**
 * MovieDetailsPage — Movie detail screen
 * Figma: 1.1 Movie Details (Desktop) (24654:5195)
 *
 * Data flow:
 *   1. Fetch movie detail by ID from URL param
 *   2. Resolve genres + age rating
 *   3. Fetch similar/related content
 *   4. Fetch trailer URL
 *
 * Sections:
 *   1. Hero (bg image, title, metadata, genres, description, rent/play, credits)
 *   2. Casts & Crews (parsed from cast string)
 *   3. More like this (related movies)
 *   4. Synopsis / Information / Languages
 *   5. Footer
 */

const MovieDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const movieDetails = useSelector((state) => state.content.movieDetails);
  const similarMovies = useSelector((state) => state.content.similarMovies);
  const loading = useSelector((state) => state.content.loaders.movieDetails);
  // Authoritative list of CRM product IDs the user currently has active orders for.
  // Populated by fetchPurchaseHistory() and persisted in localStorage.
  const activeProductIds = useSelector((state) => state.subscription.activeProductIds);

  // Price passed from /rent grid takes precedence over packages-UID re-fetch
  const navStateRentalPrice = location.state?.rentalPrice ?? null;
  const navStateTvStoreProductId = location.state?.tvStoreProductId ?? null;
  const fromRent = location.state?.fromRent === true;

  // Determine which nav tab should be active based on where the user came from
  const inferActiveTab = () => {
    // Explicit override from navigation state
    if (location.state?.fromTab) return location.state.fromTab;
    // Infer from fromRent flag
    if (fromRent) return "Rent";
    // Infer from referrer path stored by React Router
    const prev = location.state?.from || "";
    if (prev.startsWith("/discover")) return "Discover";
    if (prev.startsWith("/rent")) return "Rent";
    if (prev.startsWith("/live")) return "Live Show";
    if (prev.startsWith("/podcast")) return "Podcast";
    if (prev.startsWith("/home")) return "";
    return "";
  };
  const navActiveTab = inferActiveTab();

  const [tvStoreProductId, setTvStoreProductId] = useState(navStateTvStoreProductId);

  const [genreNames, setGenreNames] = useState([]);
  const [ageRatingLabel, setAgeRatingLabel] = useState("");
  const [trailerUrl, setTrailerUrl] = useState(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [rentalPrice, setRentalPrice] = useState(navStateRentalPrice);
  const [trailerActive, setTrailerActive] = useState(false);
  // True once the TV Store match attempt has completed (or was seeded from nav state).
  // Gates button label rendering to prevent Play → Rent → Rent ₦X flicker on load.
  const [rentalResolved, setRentalResolved] = useState(
    navStateTvStoreProductId != null || navStateRentalPrice != null
  );
  // True after fetchPurchaseHistory resolves so activeProductIds reflects the
  // current CRM state. Without this, poster-card navigations render a seeded
  // "Rent ₦X" first, then flip to "Play" once the fresh history loads.
  const [purchaseHistoryReady, setPurchaseHistoryReady] = useState(false);
  // Local snapshot of the movie we're viewing. Pinned once we've seen the
  // matching id come through Redux — protects against other components
  // (HomePage hero slide builder, etc) overwriting the shared movieDetails slice.
  const [localMovie, setLocalMovie] = useState(null);
  const [trailerMuted, setTrailerMuted] = useState(true);
  const trailerVideoRef = useRef(null);
  const heroSectionRef = useRef(null);

  const handleToggleMute = () => {
    setTrailerMuted((prev) => {
      const next = !prev;
      console.log("[MovieDetailsPage] trailer mute toggled →", next);
      if (trailerVideoRef.current) trailerVideoRef.current.muted = next;
      return next;
    });
  };

  const handleToggleFullscreen = () => {
    const el = heroSectionRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      console.log("[MovieDetailsPage] exiting fullscreen");
      document.exitFullscreen();
    } else {
      console.log("[MovieDetailsPage] entering fullscreen on hero");
      el.requestFullscreen?.();
    }
  };

  // Auto-play trailer when URL is available
  useHls(trailerVideoRef, trailerUrl, { autoPlay: true });

  // Activate trailer display once URL loads
  useEffect(() => {
    if (trailerUrl) setTrailerActive(true);
  }, [trailerUrl]);

  // Fetch movie detail + refresh active subscription list (so isPurchased is fresh)
  useEffect(() => {
    if (!id) return;
    console.log("[MovieDetailsPage] Fetching movie:", id);
    // Reset local snapshot + readiness on each new movie id so old state doesn't flash
    setLocalMovie(null);
    setRentalResolved(navStateTvStoreProductId != null || navStateRentalPrice != null);
    setPurchaseHistoryReady(false);
    fetchMovieDetail(id);
    fetchSimilarContent(id, "movies");
    fetchPurchaseHistory("All").finally(() => setPurchaseHistoryReady(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Pin a local snapshot once Redux movieDetails matches this page's id —
  // subsequent Redux overwrites from other pages can't clobber us.
  useEffect(() => {
    if (!movieDetails || !id) return;
    if (String(movieDetails.id) !== String(id)) return;
    if (localMovie && String(localMovie.id) === String(id)) return;
    console.log("[MovieDetailsPage] Pinning local movie:", movieDetails.title, "id:", movieDetails.id);
    setLocalMovie(movieDetails);
  }, [movieDetails, id, localMovie]);

  // Resolve genres + age rating + trailer when the PINNED local movie loads.
  // Driven by localMovie (not Redux movieDetails) so other components' fetches
  // can't trigger spurious re-resolution.
  useEffect(() => {
    if (!localMovie) return;
    const movieDetails = localMovie;

    console.log("[MovieDetailsPage] Movie loaded:", movieDetails.title, "genres:", movieDetails.movie_genres, "age:", movieDetails.age_rating_id);
    setIsFavorited(movieDetails.favorited || false);

    const resolveMeta = async () => {
      const [genreMap, ageMap] = await Promise.all([fetchGenres(), fetchAgeRatings()]);

      // Genres
      const gIds = movieDetails.movie_genres || movieDetails.genres || [];
      const names = gIds.map((g) => genreMap[g.id || g] || "").filter(Boolean);
      setGenreNames(names);
      console.log("[MovieDetailsPage] Genres resolved:", names.join(", "));

      // Age rating
      if (movieDetails.age_rating_id && ageMap[movieDetails.age_rating_id]) {
        setAgeRatingLabel(ageMap[movieDetails.age_rating_id]);
      }

      // Trailer
      if (movieDetails.cm_trailer || movieDetails.trailer_url_ott) {
        const url = await fetchTrailer(movieDetails.id, "movie");
        if (url) setTrailerUrl(url);
      }

      // TV Store product match — resolves tvStoreProductId + rentalPrice for ANY movie
      // whose packages map to a TV Store product. Previously gated on movie.promotable,
      // but the flag flips to false for already-rented items AND for some backend-state
      // edge cases, which broke the purchase-detection path (fell through to the stale
      // movie.purchased flag). Always attempt the match — isPurchased is then derived
      // from activeProductIds (CRM source of truth), not from this response.
      const pkgUids = (movieDetails.packages || []).map((p) => p.uid).filter(Boolean);
      if (pkgUids.length > 0) {
        try {
          const accessToken = localStorage.getItem("mango_access_token");
          const userId = localStorage.getItem("mango_user_id");
          const storeRes = await fetch(`${getClientApiUrl()}/api/client/v1/9mobile/users/${userId}/tv_store`, {
            headers: { Authorization: `Bearer ${accessToken}` },
          });
          const storeData = await storeRes.json();
          if (storeData.status === "ok") {
            const matchedProduct = storeData.data?.find((p) => pkgUids.includes(p.uid));
            if (matchedProduct) {
              const price = matchedProduct.price_options?.[0]?.activation_price;
              setRentalPrice(price);
              setTvStoreProductId(matchedProduct.id);
              // Note: we intentionally no longer set rentalPurchased from this response —
              // price_options[].purchased is unreliable (backend returns true for all TVOD).
              // isPurchased is computed downstream from activeProductIds (CRM).
              console.log("[MovieDetailsPage] TV Store product matched:", { price, productId: matchedProduct.id, matchedUid: matchedProduct.uid, action: matchedProduct.action });
            } else {
              console.log("[MovieDetailsPage] No TV Store match for package UIDs:", pkgUids);
            }
          }
        } catch (e) {
          console.warn("[MovieDetailsPage] Failed to fetch TV Store:", e.message);
        }
      }
      // Mark resolution complete — button can now render its final label.
      setRentalResolved(true);
    };

    resolveMeta();
  }, [localMovie]);

  // Derived values — use the pinned local snapshot (stable against Redux pollution).
  const movie = localMovie;
  // Image-store lookup helpers — the movie detail endpoint exposes preview /
  // title-art ids inside `movie_image_stores: [{image_store_id, type}]` while
  // the content-category endpoint exposes them as flat `preview_image_id` /
  // `title_art_image_id` fields. Support both shapes.
  const stores = Array.isArray(movie?.movie_image_stores) ? movie.movie_image_stores : [];
  const previewStoreId = stores.find((s) => s.type === "PREVIEW")?.image_store_id;
  const titleArtStoreId = stores.find((s) => s.type === "TITLE_ART")?.image_store_id;
  // Hero background: prefer landscape preview, fall back to portrait, then asset.
  const heroImageId =
    movie?.preview_image_id ||
    previewStoreId ||
    movie?.image_store_id ||
    movie?.image_id;
  const heroImage = heroImageId
    ? getImageUrl({ image_id: heroImageId })
    : "/assets/movie-details/ted-lasso-hero.webp";
  // Title art: stylized title image overlay (replaces text H1 when present)
  const titleArtId = movie?.title_art_image_id || titleArtStoreId;
  const titleArtUrl = titleArtId
    ? getImageUrl({ image_id: titleArtId })
    : null;
  const durationStr = movie?.duration ? `${Math.floor(movie.duration / 60)}h ${movie.duration % 60}m` : "";
  const castList = movie?.cast ? movie.cast.split(",").map((name, i) => ({ name: name.trim(), role: "" })) : [];
  const isPromotable = movie?.promotable;
  // For TVOD: source of truth is CRM purchase history → activeProductIds.
  // movie.purchased and TV Store price_options[].purchased both return true for
  // every TVOD item regardless of actual rental state (backend bug), so they
  // can't be trusted. Only a matching CRM product_id in activeProductIds
  // indicates the user has actually rented/subscribed to this content.
  const hasRentalContext = fromRent || tvStoreProductId != null || rentalPrice != null;
  const isPurchasedByActiveOrder =
    tvStoreProductId != null &&
    Array.isArray(activeProductIds) &&
    activeProductIds.some((pid) => String(pid) === String(tvStoreProductId));
  // For SVOD (no rental context): trust movie.purchased as the SVOD package
  // endpoint reliably reflects bouquet access.
  const isPurchased = hasRentalContext
    ? isPurchasedByActiveOrder
    : !!movie?.purchased;

  const handlePlayOrRent = () => {
    if (isPurchased) {
      console.log("[MovieDetailsPage] Play clicked → player", { rentalContext: hasRentalContext, isPurchased });
      navigate(`/player/${movie.uid || movie.id}?type=movie`, { state: { movieId: movie.id, title: movie.title, isPurchased: true } });
      return;
    }
    if (tvStoreProductId) {
      console.log("[MovieDetailsPage] Rent clicked → PickPlanPage", { tvStoreProductId });
      navigate(`/discover/product/${tvStoreProductId}/plan`, {
        state: {
          movieUid: movie?.uid,
          movieId: movie?.id,
        },
      });
      return;
    }
    console.log("[MovieDetailsPage] Rent clicked but no tvStoreProductId resolved — fallback to subscription page");
    navigate("/settings/subscription");
  };

  const handleFavoriteToggle = async () => {
    if (!movie) return;
    if (isFavorited) {
      await removeFromWatchlist(movie.id, "movie");
      setIsFavorited(false);
    } else {
      await addToWatchlist(movie.id, "movie");
      setIsFavorited(true);
    }
  };

  // Loading state
  if (loading && !movie) {
    return (
      <div className="md-page">
        <Navbar activeTab={navActiveTab} />
        <div className="md-page__loader">
          <div className="md-page__loader-ring" />
        </div>
        <Footer />
      </div>
    );
  }

  console.log("[MovieDetailsPage] rendering:", movie?.title, "purchased:", isPurchased, "similar:", similarMovies.length, "heroImageId:", heroImageId, "titleArt:", !!titleArtUrl);

  return (
    <div className="md-page">
      <Navbar activeTab={navActiveTab} />

      {/* Hero Section */}
      <section className="md-page__hero" ref={heroSectionRef}>
        <div className="md-page__hero-bg">
          <img
            src={heroImage}
            alt={movie?.title || ""}
            className={`md-page__hero-bg-img ${trailerActive ? "md-page__hero-bg-img--hidden" : ""}`}
          />
          <video
            ref={trailerVideoRef}
            className={`md-page__hero-bg-video ${trailerActive ? "md-page__hero-bg-video--visible" : ""}`}
            muted={trailerMuted}
            playsInline
            onEnded={() => setTrailerActive(false)}
          />
        </div>
        <div className="md-page__hero-gradient" />

        <div className="md-page__hero-content">
          <div className="md-page__hero-left">
            {titleArtUrl ? (
              <img
                src={titleArtUrl}
                alt={movie?.title || ""}
                className="md-page__title-art"
              />
            ) : (
              <h1 className="md-page__show-title">{movie?.title || "Loading..."}</h1>
            )}

            {/* Actions */}
            <div className="md-page__actions">
              {(() => {
                const buttonReady = rentalResolved && purchaseHistoryReady;
                return (
                  <button
                    className="md-page__rent-btn"
                    onClick={handlePlayOrRent}
                    disabled={!buttonReady}
                  >
                    {buttonReady ? (
                      <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
                        <path d="M1 1L13 8L1 15V1Z" fill="#0a0a0a" />
                      </svg>
                    ) : (
                      <span className="md-page__rent-btn-spinner" aria-hidden="true" />
                    )}
                    {buttonReady
                      ? (isPurchased
                          ? "Play"
                          : rentalPrice
                          ? `Rent (₦${parseFloat(rentalPrice).toLocaleString()})`
                          : isPromotable
                          ? "Rent"
                          : "Play")
                      : null}
                  </button>
                );
              })()}
              <button className={`md-page__add-btn${isFavorited ? " md-page__add-btn--active" : ""}`} onClick={handleFavoriteToggle}>
                <img
                  src={isFavorited ? "/assets/movie-details/check-circle-icon.svg" : "/assets/movie-details/add-circle-icon.svg"}
                  alt={isFavorited ? "Remove" : "Add"}
                  width="24"
                  height="24"
                />
                <span className="md-page__add-btn-label">
                  {isFavorited ? "Remove from favorites" : "Add to favorite"}
                </span>
              </button>
            </div>
          </div>

          <div className="md-page__hero-right">
            <div className="md-page__hero-controls">
              <button
                type="button"
                className="md-page__hero-control-btn"
                onClick={handleToggleFullscreen}
                aria-label="Toggle fullscreen"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              </button>
              <button
                type="button"
                className="md-page__hero-control-btn"
                onClick={handleToggleMute}
                aria-label={trailerMuted ? "Unmute trailer" : "Mute trailer"}
              >
                {trailerMuted ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  </svg>
                )}
              </button>
            </div>

            <div className="md-page__credits">
              {movie?.director && (
                <div className="md-page__credit">
                  <span className="md-page__credit-label">Director</span>
                  <span className="md-page__credit-value">{movie.director}</span>
                </div>
              )}
              {/* {movie?.cast && (
                <div className="md-page__credit">
                  <span className="md-page__credit-label">Starring</span>
                  <span className="md-page__credit-value">{movie.cast}</span>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </section>

      <div className="md-page__body">
        {/* Casts & Crews — parsed from cast comma string */}
        {castList.length > 0 && (
          <section className="md-page__cast-section">
            <h2 className="md-page__section-title">Cast &amp; Crew</h2>
            <div className="md-page__cast-list">
              {castList.map((person, i) => (
                <div key={i} className="md-page__cast-item">
                  <div className="md-page__cast-avatar md-page__cast-avatar--placeholder">
                    <span>{person.name[0]}</span>
                  </div>
                  <span className="md-page__cast-name">{person.name}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        {/*
          "More like this" section disabled.
          The related endpoint (GET /vod/movies/{id}/related) returns lean items
          with only a portrait `image_id` — no landscape preview — so the
          landscape cards rendered cropped portraits. Re-enable once the backend
          provides a landscape image on related items, or add per-item detail
          fetching to resolve each PREVIEW image.

          {similarMovies.length > 0 && (
            <section className="md-page__more-section">
              <h2 className="md-page__section-title">More like this</h2>
              <div className="md-page__more-list">
                {similarMovies.map((item, i) => {
                  const img = getImageUrl(item, "POSTER") || getImageUrl(item);
                  return (
                    <div
                      key={item.id || i}
                      className="md-page__more-card"
                      onClick={() => navigate(`/details/${item.id}`, { state: { contentType: item.type, ...(navActiveTab ? { fromTab: navActiveTab } : {}) } })}
                      style={{ cursor: "pointer" }}
                    >
                      {img ? (
                        <img src={img} alt={item.title || ""} className="md-page__more-img" />
                      ) : (
                        <div className="md-page__more-placeholder">{item.title || "?"}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        */}

        {/* Synopsis / Information / Languages */}
        <section className="md-page__info-grid">
          <div className="md-page__info-col">
            <h3 className="md-page__info-heading">Synopsis</h3>
            <p className="md-page__info-text">
              {movie?.description || "No synopsis available."}
            </p>
          </div>
          <div className="md-page__info-right">
            <div className="md-page__info-col">
              <h3 className="md-page__info-heading">Information</h3>
              {movie?.year && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Release Date</span>
                  <span className="md-page__info-value">{movie.year}</span>
                </div>
              )}
              {genreNames.length > 0 && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Genre</span>
                  <span className="md-page__info-value">{genreNames.join(", ")}</span>
                </div>
              )}
              {ageRatingLabel && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Rated</span>
                  <span className="md-page__info-value">{ageRatingLabel}</span>
                </div>
              )}
              {durationStr && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Duration</span>
                  <span className="md-page__info-value">{durationStr}</span>
                </div>
              )}
              {movie?.country?.length > 0 && (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Country</span>
                  <span className="md-page__info-value">{movie.country.join(", ")}</span>
                </div>
              )}
            </div>
            <div className="md-page__info-col">
              <h3 className="md-page__info-heading">Languages</h3>
              {movie?.audio_languages ? (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Original Audio</span>
                  <span className="md-page__info-value">
                    {Array.isArray(movie.audio_languages) ? movie.audio_languages.join(", ") : movie.audio_languages}
                  </span>
                </div>
              ) : null}
              {movie?.subtitle_languages ? (
                <div className="md-page__info-row">
                  <span className="md-page__info-label">Subtitles</span>
                  <span className="md-page__info-value">
                    {Array.isArray(movie.subtitle_languages) ? movie.subtitle_languages.join(", ") : movie.subtitle_languages}
                  </span>
                </div>
              ) : null}
              {!movie?.audio_languages && !movie?.subtitle_languages && (
                <div className="md-page__info-row">
                  <span className="md-page__info-value">No language info available</span>
                </div>
              )}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default MovieDetailsPage;
